import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { WhiteboardTab } from './WhiteboardTab';
import type { WhiteboardSummaryDto } from '../types';

interface WhiteboardTabsBarProps {
  whiteboards: WhiteboardSummaryDto[];
  selectedId: string | null;
  isLoading: boolean;
  isError: boolean;
  canManage: boolean;
  isCreating: boolean;
  onRetry: () => void;
  onSelect: (whiteboardId: string) => void;
  onCreate: () => void;
  onRename: (whiteboardId: string, name: string) => void;
  onDelete: (whiteboard: WhiteboardSummaryDto) => void;
}

/** Sheet-style tab strip at the foot of the view: one tab per whiteboard, plus a create button. */
export function WhiteboardTabsBar({
  whiteboards,
  selectedId,
  isLoading,
  isError,
  canManage,
  isCreating,
  onRetry,
  onSelect,
  onCreate,
  onRename,
  onDelete,
}: WhiteboardTabsBarProps) {
  const { t } = useTranslation('whiteboards');

  return (
    <div
      role="tablist"
      aria-label={t('tabs.label')}
      className="flex shrink-0 items-end gap-1 overflow-x-auto border-t bg-muted/30 px-2 pt-1"
      data-testid="whiteboards-tabs-bar"
    >
      {isLoading && (
        <div className="flex items-end gap-1 py-1" data-testid="whiteboards-tabs-loading">
          <Skeleton className="h-7 w-32 rounded-t-md" />
          <Skeleton className="h-7 w-28 rounded-t-md" />
        </div>
      )}

      {isError && (
        <div className="flex items-center gap-2 py-1" data-testid="whiteboards-error-message">
          <span className="text-xs text-muted-foreground">{t('error.title')}</span>
          <Button
            variant="outline"
            size="sm"
            className="h-7"
            onClick={onRetry}
            data-testid="whiteboards-retry-button"
          >
            {t('actions.retry')}
          </Button>
        </div>
      )}

      {!isLoading &&
        !isError &&
        whiteboards.map((whiteboard) => (
          <WhiteboardTab
            key={whiteboard.id}
            whiteboard={whiteboard}
            isActive={whiteboard.id === selectedId}
            canManage={canManage}
            canDelete={whiteboards.length > 1}
            onSelect={onSelect}
            onRename={onRename}
            onDelete={onDelete}
          />
        ))}

      {/* Hidden while loading: offering "add" next to skeletons presents an action before we
          know what exists, and the button is replaced anyway once the list resolves. */}
      {canManage && !isLoading && !isError && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="mb-1 size-7 shrink-0"
              aria-label={t('tabs.create')}
              disabled={isCreating}
              onClick={onCreate}
              data-testid="whiteboards-create-button"
            >
              <Plus className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('tabs.create')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
